import React, { useEffect, useState, useRef } from 'react';
import { useNavState } from '../hooks/useNavState';
import Header from './common/Header';
import Footer from './common/Footer';
import TypewriterCode from './TypewriterCode';
import VideoBackground from './VideoBackground';
import spotlightService from '../services/SpotlightService';
import { optimizeImageUrl } from '../utils/ImageUtils';
import './Landing.css';

// Platform logos
import webLogo from '../assets/platform-logos/web.png';
import rokuLogo from '../assets/platform-logos/roku.jpg';
import iosLogo from '../assets/platform-logos/ios.png';
import androidLogo from '../assets/platform-logos/android.png';
import appleTvLogo from '../assets/platform-logos/apple-tv.png';
import fireTvLogo from '../assets/platform-logos/fire-tv.png';
import androidTvLogo from '../assets/platform-logos/android-tv.png';
import vizioLogo from '../assets/platform-logos/vizio.png';

// FAQ Item Component
const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="overflow-hidden bg-gray-800 border border-gray-700 rounded-lg">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-6 py-4 text-left transition-colors duration-200 hover:bg-gray-750"
      >
        <h3 className="pr-4 text-lg font-semibold text-white">{question}</h3>
        <div
          className={`transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        >
          <svg
            className="w-5 h-5 text-teal-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 pb-4">
          <p className="leading-relaxed text-gray-300">{answer}</p>
        </div>
      </div>
    </div>
  );
};

const Landing = () => {
  const { navigateTo } = useNavState();
  const [isVisible, setIsVisible] = useState(false);
  const [moviesCategory, setMoviesCategory] = useState(null);
  const [showsCategory, setShowsCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  // Refs for scroll containers
  const moviesScrollRef = useRef(null);
  const showsScrollRef = useRef(null);

  useEffect(() => {
    setIsVisible(true);
    fetchLandingCategories();
  }, []);

  const scrollLeft = scrollRef => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -400, behavior: 'smooth' });
    }
  };

  const scrollRight = scrollRef => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 400, behavior: 'smooth' });
    }
  };

  const fetchLandingCategories = async () => {
    try {
      // Fetch specific categories using getCategoryChannelsBySlug
      const [moviesData, showsData] = await Promise.all([
        spotlightService.getCategoryChannelsBySlug('landing-movies').catch(err => {
          console.warn('🔍 Landing: landing-movies category not found:', err);
          return null;
        }),
        spotlightService.getCategoryChannelsBySlug('landing-shows').catch(err => {
          console.warn('🔍 Landing: landing-shows category not found:', err);
          return null;
        }),
      ]);

      // Transform the data to match expected structure
      const moviesCategory = moviesData
        ? {
            name: 'Landing Movies',
            slug: 'landing-movies',
            channels: moviesData.channels || moviesData,
          }
        : null;

      const showsCategory = showsData
        ? {
            name: 'Landing Shows',
            slug: 'landing-shows',
            channels: showsData.channels || showsData,
          }
        : null;

      setMoviesCategory(moviesCategory);
      setShowsCategory(showsCategory);
    } catch (error) {
      console.error('❌ Landing: Error fetching landing categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartWatching = () => {
    // Navigate to the main app homepage
    navigateTo('/');
  };

  const handleMoreInfo = () => {
    // Navigate to about page
    navigateTo('/about');
  };

  const handleRailClick = () => {
    // Navigate to homepage
    navigateTo('/');
  };

  const renderContentRail = (category, fallbackItems, scrollRef) => {
    if (!category || !category.channels || category.channels.length === 0) {
      // Fallback to placeholder items if no category data
      return (
        <div className="relative">
          {/* Left Arrow */}
          <button
            onClick={() => scrollLeft(scrollRef)}
            className="absolute left-0 z-10 p-3 text-white transition-all duration-300 transform -translate-y-1/2 rounded-full top-1/2 bg-black/70 hover:bg-black/90 hover:scale-110"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          {/* Right Arrow */}
          <button
            onClick={() => scrollRight(scrollRef)}
            className="absolute right-0 z-10 p-3 text-white transition-all duration-300 transform -translate-y-1/2 rounded-full top-1/2 bg-black/70 hover:bg-black/90 hover:scale-110"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <div
            ref={scrollRef}
            className="flex gap-6 pb-4 overflow-x-auto scrollbar-hide scroll-smooth"
          >
            {fallbackItems.map((item, index) => (
              <div
                key={index}
                className="relative h-48 p-6 transition-all duration-300 border-2 border-transparent rounded-lg cursor-pointer min-w-80 bg-gradient-to-br from-gray-800 to-gray-900 hover:scale-105 hover:border-teal-500 group"
                onClick={handleRailClick}
              >
                <div className="flex flex-col justify-center h-full">
                  <h3 className="mb-3 text-xl font-semibold text-white">{item.title}</h3>
                  <p className="text-sm leading-relaxed text-gray-400">{item.desc}</p>
                </div>
                <div className="absolute text-4xl transition-opacity top-6 right-6 opacity-30 group-hover:opacity-50">
                  {item.icon}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Render actual content rail without metadata overlay
    return (
      <div className="relative">
        {/* Left Arrow */}
        <button
          onClick={() => scrollLeft(scrollRef)}
          className="absolute left-0 z-10 p-3 text-white transition-all duration-300 transform -translate-y-1/2 rounded-full top-1/2 bg-black/70 hover:bg-black/90 hover:scale-110"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        {/* Right Arrow */}
        <button
          onClick={() => scrollRight(scrollRef)}
          className="absolute right-0 z-10 p-3 text-white transition-all duration-300 transform -translate-y-1/2 rounded-full top-1/2 bg-black/70 hover:bg-black/90 hover:scale-110"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <div
          ref={scrollRef}
          className="flex gap-6 pb-4 overflow-x-auto scrollbar-hide scroll-smooth"
        >
          {category.channels.slice(0, 12).map((channel, index) => {
            const imageUrl = optimizeImageUrl(
              channel.poster ||
                channel.spotlight_poster ||
                channel.image?.poster ||
                channel.image?.spotlight_poster,
              { width: 300, height: 450 },
            );

            return (
              <div
                key={channel._id || index}
                className="relative overflow-hidden transition-all duration-300 border-2 border-transparent rounded-lg cursor-pointer min-w-48 h-72 bg-gradient-to-br from-gray-800 to-gray-900 hover:scale-105 group"
                onClick={handleRailClick}
              >
                {imageUrl && (
                  <img
                    src={imageUrl}
                    alt={channel.title}
                    className="object-cover w-full h-full"
                    loading="lazy"
                  />
                )}
                {/* Removed metadata overlay - no title or description */}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen overflow-x-hidden text-white bg-black">
      <Header />

      {/* Hero Section */}
      {/* Video uploaded to dotstudiopro2021 bucket in AWS */}
      <VideoBackground
        className="flex items-center justify-center h-screen pt-20 sm:pt-0"
        videoSrc="https://d313yktdr0s2gj.cloudfront.net/chillfree-assets/hero-background.mp4"
        posterSrc="/assets/images/hero-poster.jpg" // Optional poster image
      >
        <div className="grid items-center w-full h-full grid-cols-1 gap-16 px-6 mx-auto max-w-7xl lg:grid-cols-2 justify-items-center">
          {/* Hero Text */}
          <div
            className={`transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
            }`}
          >
            <span className="inline-block px-4 py-2 mb-8 text-sm font-semibold tracking-wider text-teal-400 uppercase border border-teal-500 rounded-full bg-teal-500/20">
              🤖 World's First AI-Built Platform
            </span>

            <h1 className="mb-6 text-6xl font-black leading-none text-transparent md:text-8xl bg-gradient-to-r from-white to-gray-300 bg-clip-text">
              ChillFree TV
            </h1>

            <h2 className="mb-8 text-2xl font-light text-gray-300 md:text-3xl">
              Classic Entertainment. Futuristic Technology.
            </h2>

            <p className="max-w-lg mb-12 text-lg leading-relaxed text-gray-400">
              Thousands of timeless movies and TV shows from the golden era, powered by cutting-edge
              AI. Built entirely with Claude 4.0, Manus, and Cursor through revolutionary "vibe
              coding."
            </p>

            <div className="flex flex-col gap-4 sm:flex-row">
              <button
                onClick={handleStartWatching}
                className="flex items-center justify-center gap-3 px-8 py-4 text-lg font-semibold text-black transition-all duration-300 bg-white rounded-lg hover:bg-gray-200 hover:scale-105"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                    clipRule="evenodd"
                  />
                </svg>
                Start Watching
              </button>

              <button
                onClick={handleMoreInfo}
                className="flex items-center justify-center gap-3 px-8 py-4 text-lg font-semibold text-white transition-all duration-300 rounded-lg bg-gray-700/70 hover:bg-gray-600/70"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                More Info
              </button>
            </div>
          </div>

          {/* Hero Visual - Code Preview */}
          <div
            className={`transition-all duration-1000 delay-300 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
            }`}
          >
            <div className="w-full max-w-lg overflow-hidden bg-gray-900 border border-gray-700 shadow-2xl rounded-xl">
              {/* Terminal Header */}
              <div className="flex items-center gap-3 px-6 py-4 bg-gray-800">
                <div className="flex gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                </div>
                <span className="ml-4 text-sm text-gray-400">AI Development Console</span>
              </div>

              {/* Code Content */}
              <TypewriterCode />
            </div>
          </div>
        </div>
      </VideoBackground>

      {/* Content Rows Section */}
      <section className="py-16 bg-black">
        <div className="px-6 mx-auto max-w-7xl">
          {/* Classic Movies Row */}
          <div className="mb-16">
            <h2 className="mb-6 text-3xl font-bold text-white">🎬 Classic Movies</h2>
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="text-gray-400">Loading classic movies...</div>
              </div>
            ) : (
              renderContentRail(
                moviesCategory,
                [
                  {
                    title: 'Golden Age Cinema',
                    desc: "Curated collection of timeless masterpieces from Hollywood's golden era",
                    icon: '🎭',
                  },
                  {
                    title: 'Film Noir Classics',
                    desc: 'Dark, moody masterpieces that defined a generation of cinema',
                    icon: '🎬',
                  },
                  {
                    title: 'Western Adventures',
                    desc: 'Epic tales from the American frontier and beyond',
                    icon: '🤠',
                  },
                ],
                moviesScrollRef,
              )
            )}
          </div>

          {/* Classic TV Shows Row */}
          <div className="mb-16">
            <h2 className="mb-6 text-3xl font-bold text-white">📺 Classic TV Shows</h2>
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="text-gray-400">Loading classic TV shows...</div>
              </div>
            ) : (
              renderContentRail(
                showsCategory,
                [
                  {
                    title: 'Vintage Sitcoms',
                    desc: 'Comedy classics that shaped television history',
                    icon: '😂',
                  },
                  {
                    title: 'Drama Series',
                    desc: "Compelling stories from television's golden age",
                    icon: '🎭',
                  },
                  {
                    title: 'Sci-Fi Classics',
                    desc: 'Retro-futuristic adventures that predicted tomorrow',
                    icon: '🚀',
                  },
                ],
                showsScrollRef,
              )
            )}
          </div>
        </div>
      </section>

      {/* FREE LIVE TV Section */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-6xl px-6 mx-auto">
          <h2 className="mb-16 text-5xl font-bold text-center text-white">
            FREE LIVE TV AT YOUR FINGERTIPS
          </h2>

          <div className="grid items-center grid-cols-1 gap-12 lg:grid-cols-2">
            {/* Left Column - Text */}
            <div className="space-y-6">
              <p className="text-xl leading-relaxed text-gray-300">
                Unlock a world of entertainment with Free Live TV—where classic movies and TV shows
                are just a tap away. Dive into a curated collection of timeless favorites,
                handpicked for your viewing pleasure. Experience nostalgia at its finest, all
                conveniently at your fingertips.
              </p>
            </div>

            {/* Right Column - Image */}
            <div className="flex justify-center">
              <img
                src="/assets/images/live-tv.png"
                alt="Live TV Electronic Program Guide"
                className="w-full max-w-lg border border-gray-700 shadow-2xl rounded-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-4xl px-6 mx-auto">
          <div className="mb-16 text-center">
            <h2 className="mb-6 text-4xl font-black text-white md:text-5xl">
              Frequently Asked Questions
            </h2>
            <p className="max-w-2xl mx-auto text-xl text-gray-300">
              Everything you need to know about ChillFree TV
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                question: 'Is ChillFree TV really free?',
                answer:
                  'Yes! ChillFree TV is completely free to use. We offer thousands of classic movies and TV shows at no cost. Our platform is supported by minimal, non-intrusive advertising that helps us keep the service free for everyone.',
              },
              {
                question: 'What kind of content is available?',
                answer:
                  'ChillFree TV specializes in classic movies and timeless TV shows from various decades. Our library includes drama, comedy, action, horror, documentaries, and more. We focus on quality content that has stood the test of time, all carefully curated by our AI-powered recommendation system.',
              },
              {
                question: 'Do I need to create an account?',
                answer:
                  'While you can browse some content without an account, creating a free profile unlocks the full ChillFree experience. With an account, you get personalized recommendations, watchlists, viewing history, and the ability to resume watching across devices.',
              },
              {
                question: 'What devices can I watch on?',
                answer:
                  'ChillFree TV works on virtually any device! Watch on your computer, smartphone, tablet, smart TV, Roku, Apple TV, Fire TV, Android TV, and more. Your viewing experience syncs seamlessly across all your devices.',
              },
              {
                question: 'How does the AI recommendation system work?',
                answer:
                  "Our advanced AI analyzes your viewing patterns, preferences, and ratings to suggest content you'll love. The more you watch, the smarter our recommendations become. We also use AI to enhance video quality and optimize streaming performance.",
              },
              {
                question: 'Is there a mobile app?',
                answer:
                  'Yes! ChillFree TV has dedicated apps for iOS and Android devices. Download them from the App Store or Google Play Store for the best mobile viewing experience with offline capabilities and push notifications.',
              },
              {
                question: 'Can I download content for offline viewing?',
                answer:
                  'Offline viewing is available on our mobile apps for select content. This feature allows you to download movies and shows to watch later without an internet connection - perfect for travel or areas with limited connectivity.',
              },
              {
                question: 'How often is new content added?',
                answer:
                  "We regularly add new classic movies and TV shows to our library. While we focus on timeless content rather than the latest releases, we're constantly expanding our collection with hidden gems and beloved classics you might have missed.",
              },
            ].map((faq, index) => (
              <FAQItem key={index} question={faq.question} answer={faq.answer} />
            ))}
          </div>

          <div className="mt-16 text-center">
            <p className="mb-6 text-gray-400">Still have questions? We're here to help!</p>
            <button
              onClick={() => navigateTo('/contact')}
              className="px-8 py-3 font-semibold text-white transition-all duration-300 bg-teal-600 rounded-lg hover:bg-teal-700 hover:scale-105"
            >
              Contact Support
            </button>
          </div>
        </div>
      </section>

      {/* AI Innovation Section */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-6xl px-6 mx-auto text-center">
          <h2 className="mb-8 text-5xl font-black text-transparent md:text-6xl bg-gradient-to-r from-teal-400 to-blue-500 bg-clip-text">
            Built by AI, Powered by Innovation
          </h2>
          <p className="max-w-4xl mx-auto mb-16 text-xl leading-relaxed text-gray-300">
            ChillFree TV represents a breakthrough in streaming technology. Every line of code,
            every feature, every pixel was crafted through collaborative "vibe coding" between
            humans and AI.
          </p>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {[
              {
                icon: '🧠',
                title: 'Claude 4.0 Integration',
                desc: 'Advanced AI reasoning powers our recommendation engine, search functionality, and user experience optimization.',
              },
              {
                icon: '⚙️',
                title: 'Manus & Cursor Development',
                desc: 'Revolutionary development tools enabled rapid prototyping and seamless AI-human collaboration.',
              },
              {
                icon: '🔗',
                title: 'dotstudioPRO API',
                desc: 'Robust backend infrastructure provides the foundation for our AI-enhanced streaming platform.',
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="p-8 transition-all duration-300 border border-gray-700 bg-gray-800/50 rounded-xl hover:-translate-y-2 hover:border-teal-500 hover:shadow-2xl hover:shadow-teal-500/20"
              >
                <div className="mb-6 text-5xl">{feature.icon}</div>
                <h3 className="mb-4 text-2xl font-semibold text-white">{feature.title}</h3>
                <p className="leading-relaxed text-gray-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 bg-black border-t border-b border-gray-800">
        <div className="max-w-6xl px-6 mx-auto">
          <div className="grid grid-cols-2 gap-8 text-center md:grid-cols-4">
            {[
              { number: '1000+', label: 'Classic Titles' },
              { number: '24/7', label: 'Live Channels' },
              { number: '100%', label: 'AI-Powered' },
              { number: 'FREE', label: 'Always' },
            ].map((stat, index) => (
              <div key={index} className="p-8">
                <div className="mb-2 text-6xl font-black text-teal-400 drop-shadow-lg">
                  {stat.number}
                </div>
                <div className="text-xl font-light tracking-wider text-gray-300 uppercase">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platforms Section */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-6xl px-6 mx-auto text-center">
          <h2 className="mb-8 text-5xl font-bold text-white">Watch Anywhere</h2>
          <p className="max-w-3xl mx-auto mb-16 text-xl text-gray-300">
            Stream seamlessly across all your devices. Classic entertainment meets modern
            convenience.
          </p>

          <div className="grid grid-cols-2 gap-6 md:grid-cols-4 lg:grid-cols-8">
            {[
              { logo: webLogo, name: 'Web', alt: 'Web Browser' },
              { logo: rokuLogo, name: 'Roku', alt: 'Roku' },
              { logo: iosLogo, name: 'iOS', alt: 'iOS' },
              { logo: androidLogo, name: 'Android', alt: 'Android' },
              { logo: appleTvLogo, name: 'Apple TV', alt: 'Apple TV' },
              { logo: fireTvLogo, name: 'Fire TV', alt: 'Amazon Fire TV' },
              { logo: androidTvLogo, name: 'Android TV', alt: 'Android TV' },
              { logo: vizioLogo, name: 'Vizio', alt: 'Vizio' },
            ].map((platform, index) => (
              <div
                key={index}
                className="flex flex-col items-center p-6 bg-gray-800 border border-gray-700 rounded-xl"
              >
                <div className="flex items-center justify-center w-16 h-16 mb-4">
                  <img
                    src={platform.logo}
                    alt={platform.alt}
                    className="object-contain max-w-full max-h-full filter brightness-90"
                  />
                </div>
                <div className="text-sm font-medium text-center text-white">{platform.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-4xl px-6 mx-auto">
          <div className="mb-16 text-center">
            <h2 className="mb-6 text-4xl font-black text-white md:text-5xl">
              Frequently Asked Questions
            </h2>
            <p className="max-w-2xl mx-auto text-xl text-gray-300">
              Everything you need to know about ChillFree TV
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                question: 'Is ChillFree TV really free?',
                answer:
                  'Yes! ChillFree TV is completely free to use. We offer thousands of classic movies and TV shows at no cost. Our platform is supported by minimal, non-intrusive advertising that helps us keep the service free for everyone.',
              },
              {
                question: 'What kind of content is available?',
                answer:
                  'ChillFree TV specializes in classic movies and timeless TV shows from various decades. Our library includes drama, comedy, action, horror, documentaries, and more. We focus on quality content that has stood the test of time, all carefully curated by our AI-powered recommendation system.',
              },
              {
                question: 'Do I need to create an account?',
                answer:
                  'While you can browse some content without an account, creating a free profile unlocks the full ChillFree experience. With an account, you get personalized recommendations, watchlists, viewing history, and the ability to resume watching across devices.',
              },
              {
                question: 'What devices can I watch on?',
                answer:
                  'ChillFree TV works on virtually any device! Watch on your computer, smartphone, tablet, smart TV, Roku, Apple TV, Fire TV, Android TV, and more. Your viewing experience syncs seamlessly across all your devices.',
              },
              {
                question: 'How does the AI recommendation system work?',
                answer:
                  "Our advanced AI analyzes your viewing patterns, preferences, and ratings to suggest content you'll love. The more you watch, the smarter our recommendations become. We also use AI to enhance video quality and optimize streaming performance.",
              },
              {
                question: 'Is there a mobile app?',
                answer:
                  'Yes! ChillFree TV has dedicated apps for iOS and Android devices. Download them from the App Store or Google Play Store for the best mobile viewing experience with offline capabilities and push notifications.',
              },
              {
                question: 'Can I download content for offline viewing?',
                answer:
                  'Offline viewing is available on our mobile apps for select content. This feature allows you to download movies and shows to watch later without an internet connection - perfect for travel or areas with limited connectivity.',
              },
              {
                question: 'How often is new content added?',
                answer:
                  "We regularly add new classic movies and TV shows to our library. While we focus on timeless content rather than the latest releases, we're constantly expanding our collection with hidden gems and beloved classics you might have missed.",
              },
            ].map((faq, index) => (
              <FAQItem key={index} question={faq.question} answer={faq.answer} />
            ))}
          </div>

          <div className="mt-16 text-center">
            <p className="mb-6 text-gray-400">Still have questions? We're here to help!</p>
            <button
              onClick={() => navigateTo('/contact')}
              className="px-8 py-3 font-semibold text-white transition-all duration-300 bg-teal-600 rounded-lg hover:bg-teal-700 hover:scale-105"
            >
              Contact Support
            </button>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-32 overflow-hidden bg-gradient-to-br from-teal-500 to-teal-600">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative z-10 max-w-4xl px-6 mx-auto text-center">
          <h2 className="mb-8 text-5xl font-black text-black md:text-6xl">
            Ready to Experience the Future?
          </h2>
          <p className="max-w-3xl mx-auto mb-12 text-2xl text-black/80">
            Join the streaming revolution. Thousands of classic movies and shows await, powered by
            the world's most advanced AI technology.
          </p>
          <button
            onClick={handleStartWatching}
            className="px-12 py-6 text-2xl font-bold tracking-wider text-teal-400 uppercase transition-all duration-300 bg-black rounded-lg hover:scale-105 hover:shadow-2xl hover:bg-gray-900"
          >
            Start Watching Now
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Landing;
